import React, { useEffect, useState } from 'react';

import { Alert, Button, Col, Divider, Form, Input, Modal, Row } from 'antd';
import { useTranslation } from 'react-i18next';

interface ChangePasswordModalProps {
  isOpen: boolean;
  handleModalCancel: () => void;
  submit: (companyId: number, newPassword: string) => void;
  companyName: string;
  companyId: number;
}

function ChangePasswordModal({
  isOpen,
  handleModalCancel,
  submit,
  companyName,
  companyId,
}: ChangePasswordModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordReadOnly, setPasswordReadOnly] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setPasswordReadOnly(true);
    }
  }, [isOpen]);

  const onFinish = async (values: any) => {
    setLoading(true);

    await submit(companyId, values.newPassword);

    setLoading(false);
  };

  const handleRemoveReadOnly = () => {
    setPasswordReadOnly(false);
  };

  return (
    <Modal
      title={t(
        'scenes.settings.companies.components.change_password_modal.title',
        { name: companyName }
      )}
      open={isOpen}
      onCancel={handleModalCancel}
      maskClosable={false}
      destroyOnClose
      footer={null}
      width={480}
    >
      <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

      <Alert
        message={t(
          'scenes.settings.companies.components.change_password_modal.alert_info'
        )}
        type="info"
        showIcon
        style={{ marginBottom: '16px' }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
      >
        <Divider
          orientation="center"
          style={{ marginTop: '16px', marginBottom: '16px' }}
        >
          {t(
            'scenes.settings.companies.components.change_password_modal.labels.encrypted_reports'
          )}
        </Divider>

        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              name="newPassword"
              label={t(
                'scenes.settings.companies.components.change_password_modal.labels.new_password'
              )}
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.reject(
                        new Error(
                          t(
                            'scenes.settings.companies.components.change_password_modal.validations.new_password.required'
                          )
                        )
                      );
                    }
                    if (value.length < 8) {
                      return Promise.reject(
                        new Error(
                          t(
                            'scenes.settings.companies.components.new_company_modal.validations.password.min_length'
                          )
                        )
                      );
                    }
                    if (!/[A-Za-z]/.test(value)) {
                      return Promise.reject(
                        new Error(
                          t(
                            'scenes.settings.companies.components.new_company_modal.validations.password.letter_required'
                          )
                        )
                      );
                    }
                    if (!/[0-9]/.test(value)) {
                      return Promise.reject(
                        new Error(
                          t(
                            'scenes.settings.companies.components.new_company_modal.validations.password.number_required'
                          )
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input.Password
                disabled={loading}
                placeholder={t(
                  'scenes.settings.companies.components.new_company_modal.placeholders.password'
                )}
                autoComplete="off"
                readOnly={passwordReadOnly}
                onFocus={handleRemoveReadOnly}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              name="confirmNewPassword"
              label={t(
                'scenes.settings.companies.components.change_password_modal.labels.confirm_new_password'
              )}
              dependencies={['newPassword']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value) {
                      return Promise.reject(
                        new Error(
                          t(
                            'scenes.settings.companies.components.change_password_modal.validations.confirm_new_password.required'
                          )
                        )
                      );
                    }
                    if (value !== getFieldValue('newPassword')) {
                      return Promise.reject(
                        new Error(
                          t(
                            'scenes.settings.companies.components.change_password_modal.validations.confirm_new_password.match'
                          )
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input.Password
                disabled={loading}
                placeholder={t(
                  'scenes.settings.companies.components.new_company_modal.placeholders.password'
                )}
                autoComplete="off"
                readOnly={passwordReadOnly}
                onFocus={handleRemoveReadOnly}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24}>
            <Button
              onClick={() => {
                form.submit();
              }}
              loading={loading}
              type="primary"
              block
            >
              {t(
                'scenes.settings.companies.components.change_password_modal.buttons.save_changes'
              )}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default ChangePasswordModal;
